import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import React, {useEffect, useState} from 'react'
import {Alert, Avatar, Button, Grid, Paper} from '@mui/material'
import {useParams} from 'react-router-dom'
import {useNavigate} from 'react-router-dom'
import DeckApi from '../logic/DeckApi'
import {Card, Deck} from '../model/models'
import ImageSelector from '../components/ImageSelector'
import LoadingBackdrop from '../components/LoadingBackdrop'
import ImageLogic from '../logic/ImageLogic'
import DeckLogic from '../logic/DeckLogic'
import Button2 from '../components/Button2'
import {pink} from '@mui/material/colors'
import CloseIcon from '@mui/icons-material/Close'
import {Link} from 'react-router-dom'
import {useSnackbar} from 'notistack'
import ApiResponse from '../model/ApiResponse'
import MarkdownTextarea from '../components/MarkdownTextarea'
import MarkdownDisplay from '../components/MarkdownDisplay'
import HelpIcon from '@mui/icons-material/Help'

const api = new DeckApi()

export default function CreateAndEditCard() {
  const navigate = useNavigate()

  const {deckIdParam, cardIdParam} = useParams()
  const [loading, setLoading] = useState<boolean>(true)
  const [deck, setDeck] = useState<Deck | null>(null)
  const [frontText, setFrontText] = useState<string>('')
  const [backText, setBackText] = useState<string>('')
  const [frontImageFile, setFrontImageFile] = useState<any>(null)
  const [backImageFile, setBackImageFile] = useState<any>(null)
  const [updateFrontImage, setUpdateFrontImage] = useState<boolean>(false)
  const [updateBackImage, setUpdateBackImage] = useState<boolean>(false)
  const [generalErrors, setGeneralErrors] = useState<string[]>([])
  const [refreshNumber, setRefreshNumber] = useState<number>(0)
  const [deckId, setDeckId] = useState<number>(-1)
  const [cardId, setCardId] = useState<number>(-1)
  const [cardForEditingLoaded, setCardForEditingLoaded] = useState<boolean>(false)
  const [editMode, setEditMode] = useState<boolean | null>(null)
  const [initialFrontImagePreviewUrl, setInitialFrontImagePreviewUrl] = useState<string>('')
  const [initialBackImagePreviewUrl, setInitialBackImagePreviewUrl] = useState<string>('')
  const {enqueueSnackbar} = useSnackbar()

  useEffect(() => {
    if (!deckIdParam) {
      throw Error('deckIdParam not present')
    }

    const deckIdInt = parseInt(deckIdParam)
    setDeckId(deckIdInt)

    let apiCalls: Promise<any>[] = [api.getDeckWithoutCards(deckIdInt)]

    if (!!cardIdParam) {
      setEditMode(true)
      const cardIdInt = parseInt(cardIdParam)
      setCardId(cardIdInt)
      apiCalls.push(api.getCard(deckIdInt, cardIdInt))
    } else {
      setEditMode(false)
    }

    Promise.all(apiCalls)
      .then(values => {
        const deckResponse = values[0] as ApiResponse<Deck | null>
        if (deckResponse.isError()) {
          console.error('error getting deck', deckResponse.exception?.toJson())
          navigate('/error')
          return
        }
        setDeck(deckResponse.data)

        if (values.length > 1) {
          const cardResponse = values[1] as ApiResponse<Card | null>
          if (cardResponse.isError()) {
            console.error('error getting card', cardResponse.exception?.toJson())
            navigate('/error')
            return
          }
          populateCardFromApi(cardResponse.data!)
        }
      })
      .catch(err => {
        console.error('error getting deck and/or card', err)
        navigate('/error')
      })
  }, [])

  useEffect(() => {
    if (deck === null || editMode === null) {
      return
    }
    if (editMode && !cardForEditingLoaded) {
      return
    }
    setLoading(false)
  }, [deck, cardId, editMode, cardForEditingLoaded])

  const populateCardFromApi = (card: Card) => {
    setFrontText(card.frontText)
    setBackText(card.backText)
    if (card.frontImageMeta) {
      setInitialFrontImagePreviewUrl(ImageLogic.getImageUrl(card.frontImageMeta))
    }
    if (card.backImageMeta) {
      setInitialBackImagePreviewUrl(ImageLogic.getImageUrl(card.backImageMeta))
    }
    setCardForEditingLoaded(true)
  }

  const frontImageChange = (imageFile: any) => {
    setFrontImageFile(imageFile)
    setUpdateFrontImage(true)
  }

  const backImageChange = (imageFile: any) => {
    setBackImageFile(imageFile)
    setUpdateBackImage(true)
  }

  const saveCard = async (ev: React.MouseEvent<HTMLButtonElement>, addAnother: boolean = false) => {
    ev.preventDefault()

    if (loading) {
      return
    }

    setLoading(true)
    setGeneralErrors([])

    if (!editMode) {
      const res = await api.createCard(deck!.id, frontText, backText, frontImageFile, backImageFile)
      if (res.isError()) {
        const ex = res.exception!
        if (ex.code === 400 && ex.message && ex.message.includes('card limit')) {
          setGeneralErrors([ex.message.replaceAll('"', '')])
          setLoading(false)
          return
        }
        if (ex.code === 400) {
          if (!!ex.errors['General']) {
            setGeneralErrors(ex.errors['General'] as unknown as string[])
          } else {
            setGeneralErrors(['unexpected server error - try again later'])
          }
        } else {
          setGeneralErrors(['unexpected server error - try again later'])
        }
        setLoading(false)
        return
      }
      if (addAnother) {
        reset()
        enqueueSnackbar('Card saved', {variant: 'success'})
        window.scrollTo(0, 0)
      } else {
        navigate(`/deck/${deckId}`)
      }
    } else {
      const res = await api.updateCard(deck!.id, cardId, frontText, backText, frontImageFile, backImageFile, updateFrontImage, updateBackImage)
      if (res.isError()) {
        console.error(res.exception?.toJson())
        setGeneralErrors(prev => [...prev, 'unexpected error updating card'])
        return
      }
      navigate(`/deck/${deckId}`)
    }
  }

  const reset = () => {
    setFrontText('')
    setBackText('')
    setFrontImageFile(null)
    setBackImageFile(null)
    setRefreshNumber(prev => prev + 1)
    setLoading(false)
  }

  return (
    <Box>
      <LoadingBackdrop loading={loading}/>

      <Grid container sx={{my: 2}}>
        <Link to={`/deck/${deckId}`} style={{textDecoration: "none", color: "inherit"}}>
          <Grid item sx={{display: "flex", alignText: "top", alignItems: "top"}} md={12}>
            <Avatar variant='rounded' src={DeckLogic.getImageUrl(deck)} sx={{width: 60, height: 40}}/>
            <Typography
              variant="h4"
              sx={{ml: 2}}>
              {deck && deck.title}
            </Typography>
          </Grid>
        </Link>
      </Grid>

      <Grid container>
        <Grid item xs={12}>

          <form noValidate autoComplete='off'>
            <Grid container>

              <Grid item xs={12} md={6} sx={{p: {sx: 0, md: 1}}}>
                <Box sx={{p: 1, border: '1px dashed grey'}}>
                  <Typography variant="h5">
                    Front of Card
                  </Typography>

                  <ImageSelector
                    label="front image"
                    change={imageFile => frontImageChange(imageFile)}
                    refreshNumber={refreshNumber}
                    initialImagePreviewUrl={initialFrontImagePreviewUrl}
                  />
                  <Box display='flex' sx={{justifyContent: 'space-between'}}>
                    <Typography gutterBottom variant="h6">
                      Front Text
                    </Typography>
                    <MarkdownHelpLink/>
                  </Box>

                  <MarkdownTextarea value={frontText} onChange={value => setFrontText(value)}/>
                  <Typography variant="h6">
                    Preview
                  </Typography>
                  <Paper sx={{flexGrow: 1, p: 1, minHeight: "150px"}}>
                    <MarkdownDisplay value={frontText}/>
                  </Paper>
                </Box>
              </Grid>
              <Grid item xs={12} md={6} sx={{p: {sx: 0, md: 1}}}>
                <Box sx={{p: 1, border: '1px dashed grey'}}>

                  <Typography variant="h5">
                    Back of Card (Answer)
                  </Typography>

                  <ImageSelector
                    label="back image"
                    change={imageFile => backImageChange(imageFile)}
                    refreshNumber={refreshNumber}
                    initialImagePreviewUrl={initialBackImagePreviewUrl}
                  />
                  <Box display='flex' sx={{justifyContent: 'space-between'}}>
                    <Typography gutterBottom variant="h6">
                      Back Text
                    </Typography>
                    <MarkdownHelpLink/>
                  </Box>
                  <MarkdownTextarea value={backText} onChange={value => setBackText(value)}/>
                  <Typography variant="h6">
                    Preview
                  </Typography>
                  <Paper
                    sx={{
                      flexGrow: 1, p: 1, minHeight: "150px"
                    }}>
                    <MarkdownDisplay value={backText}/>
                  </Paper>
                </Box>
              </Grid>
            </Grid>

            <Grid container spacing={1} alignItems={'center'} sx={{mt: 3}}>
              {!editMode && <Grid item xs={12} md={4}>
                <Button
                  onClick={e => saveCard(e, true)}
                  type="button"
                  variant="contained"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    fontSize: '16px',
                    textTransform: 'none',
                    width: 1,
                    fontWeight: 900,
                    maxWidth: {md: '350px'}
                  }}
                >
                  Save and Create Another Card
                </Button>
              </Grid>}
              {!editMode && <Grid item xs={12} md={4}>
                <Button
                  onClick={e => saveCard(e)}
                  type="button"
                  variant="contained"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    fontSize: '16px',
                    textTransform: 'none',
                    width: 1,
                    fontWeight: 900,
                    maxWidth: {md: '350px'}
                  }}

                >
                  Save and Return to Deck
                </Button>
              </Grid>}
              {editMode && <Grid item xs={12} md={4}>
                <Button
                  onClick={e => saveCard(e)}
                  type="button"
                  variant="contained"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    fontSize: '16px',
                    textTransform: 'none',
                    width: 1,
                    fontWeight: 900
                  }}
                >
                  Save
                </Button>
              </Grid>}
              <Grid item xs={12} md={4}>
                <Box
                  sx={{maxWidth: {xs: 1}}}>
                  <Button2
                    onClick={_ => navigate(`/deck/${deckId}`)}
                    startIcon={<CloseIcon sx={{color: pink[600]}}/>}
                  >Cancel</Button2>
                </Box>
              </Grid>
            </Grid>

          </form>

          {generalErrors.length > 0 && <Box sx={{pt: '15px'}}>
            {generalErrors.map((e, i) => <Alert key={i} sx={{mb: 1.5}} severity="error">{e}</Alert>)}
          </Box>}

        </Grid>
      </Grid>
    </Box>)
}

function MarkdownHelpLink() {
  return (
    <Link to="/markdown-help" style={{textDecoration: 'none'}} target="_blank" rel="noopener noreferrer">
      <Button sx={{textTransform: 'none',}} startIcon={<HelpIcon/>}>Markdown</Button>
    </Link>
  )
}
